import { memo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { withNamespaces } from "react-i18next";
import GeneralInfo from "./components/general-info";
import FinancialSummary from "./components/financial-summary";
import ManagementTeam from "./components/management-team";
import Shareholders from "./components/shareholders";
import UltimateShareholders from "./components/ultimate-shareholders";
import Documents from "./components/documents";
import ShareholderGraph from "./components/shareholder-graph";
import Subsidiaries from "./components/subsidiaries";
import { Button, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";
import DateUtils from "src/services/utils/DateUtils";
import { now } from "moment";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";
import { useQuery } from "@tanstack/react-query";
import SupplierContactPersonService from "src/modules/3rd-party-management/apis/admin/SupplierContactPersonService";
import { toast } from "react-toastify";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import { pdf } from "@react-pdf/renderer";
import CompanyInfo from "./exports/CompanyInfo";
import { saveAs } from "file-saver";
import { withRouter } from "react-router-dom/cjs/react-router-dom";
import { connect } from "react-redux";
import BasicInfosHeader from "../../components/basic-infos-header";

const CompanyInfoDetail = ({ t, detailsQuery, supplierData, ...elseData }) => {
    const history = useHistory();

    const [activeAccordion, setActiveAccordion] = useState(null);
    const source="company_info"
    const { idInSource } = detailsQuery?.data?.company;

    const DetailErrorMessages = {
        "insufficient_credit_kyb": "You don’t have enough {{creditsLabel}} to complete this action.",
        "data not found or fetch failed": "The company details could not be found. It may no longer be available or accessible.",
        "unknown": "An error occurred while fetching the company details. Please try again later."
    };
    
    const {
        isFetching: contactsAreFetching,
        refetch: refetchContacts
    } = useQuery({
        queryKey: [
            '3rd-party-management-company-info-fetch-supplier-contact-people-list',
            supplierData?.id
        ],
        queryFn: async () => {
            const service = SupplierContactPersonService.getInstance();

            return await service.list(supplierData?.id, {});
        },
        enabled: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching contact people list.'), {
                type: 'error',
            });
        },
    });

    const getDataForPDFQuery = useQuery({
        queryKey: [
            '3rd-party-management-kyb-get-report-pdf-details',
            idInSource
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();
            
            return await service.fetchOrgDetailsForPDF(
                idInSource,
                source,
                supplierData?.id
            );
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: false,
        retry: 0,
        onError: ({ response }) => {
            toast(t(DetailErrorMessages[response?.data?.error] || DetailErrorMessages['unknown'], {
                creditsLabel: 'credits'
            }), {
                type: 'error'
            });
        }
    });

    const fetchExportData = async () => {
        const dateUtils = new DateUtils();
        const currentDate = dateUtils.convertDateToDate(now(), INTERNATIONAL_DATE_FORMAT)

        const fileName = `Company Info-${currentDate}.pdf`;

        const { data: contactsRes } = await refetchContacts();

        const contactPersons = contactsRes?.contactPersons 
        

        const { data: reportDataRes } = await getDataForPDFQuery.refetch();
        
        const blob = await pdf( 
            <CompanyInfo user={elseData.user} supplierData={supplierData} detailsQuery={reportDataRes} organization={elseData.organization} contactPerson={contactPersons}  />
        ).toBlob();

        saveAs(blob, fileName);
        
    }

    const exportIsInProcess = getDataForPDFQuery.isFetching || contactsAreFetching;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-5">
                <Button
                    color="secondary"
                    outline
                    onClick={() => {
                        history.push({
                            search: `?tab=kyb`
                        })
                    }}
                >
                    <i className="ri-arrow-left-line me-1" />
                    {t('Back to List')}
                </Button>


                <Button
                    color="primary"
                    onClick={() => {
                        fetchExportData()
                    }}
                    outline
                    disabled={exportIsInProcess}
                >
                    
                    {exportIsInProcess ? (
                        <>
                            <Spinner 
                                size='sm'
                                className="me-2" 
                            />
                            {t("Exporting as PDF")}...
                        </>
                    ) : (
                        <>
                            <i className="ri-download-line me-1" />
                            {t('Download PDF')}
                        </>
                    )}
                    
                </Button>
            </div>

            <div>
                <Accordion
                    className="custom-accordion"
                    activeKey={['basic-infos', activeAccordion]}
                    defaultActiveKey={activeAccordion}
                >
                    <BasicInfosHeader 
                        detailsQuery={detailsQuery}
                    />
                    
                    <GeneralInfo
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />

                    <FinancialSummary
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />

                    <ManagementTeam
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                        supplierData={supplierData}
                    />

                    <Shareholders
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                        supplierData={supplierData}
                    />

                    <UltimateShareholders
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />

                    <Subsidiaries
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />

                    <ShareholderGraph
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />

                    <Documents
                        setActiveAccordion={setActiveAccordion}
                        activeAccordion={activeAccordion}
                        detailsQuery={detailsQuery}
                    />
                </Accordion>
            </div>
        </div>
    )
}


const mapStatetoProps = (state) => {
    return {
        organization  :   state.Organization,
        user: state.Login
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(memo(CompanyInfoDetail)))
);
